// ---------------------------------------------------------------------------
// Access Control Service — Real implementation backed by the Teams Eval Data Hub API.
//
// API endpoints:
//   GET  /accesscontrolgroup/aad/search?displayName=
//   POST /accesscontrolgroup
//   GET  /accesscontrolgroup/{id}
//   PUT  /accesscontrolgroup/{id}
//   DELETE /accesscontrolgroup/{id}
//   GET  /tenantgrouprole
//   POST /tenantgrouprole
//   PUT  /tenantgrouprole
//   GET  /tenantgrouprole/me
// ---------------------------------------------------------------------------

import type { IHttpClient } from '../api/client.js';

import type {
  AccessControlGroup,
  AadGroupInfo,
  CreateAccessControlGroupRequest,
  CreateDatasetGroupRoleRequest,
  CreateTenantGroupRoleRequest,
  DataSetAccessRequestUrlResponse,
  DataSetCallerRoleResponse,
  DatasetGroupRole,
  IAccessControlService,
  MyTenantRoleResponse,
  TenantGroupRole,
  UpdateAccessControlGroupRequest,
  UpdateDatasetGroupRoleRequest,
  UpdateTenantGroupRoleRequest,
} from './accessControlService.interface.js';
import type { UsageContextOverrides } from './usageContext.interface.js';
import {
  ACCESS_CONTROL_USAGE_ACTIONS,
  optionalUsageContextRequestOptions,
} from './usageContext.js';

// ── API path constants ────────────────────────────────────────────────────────

const SEARCH_AAD_GROUPS_PATH = 'accesscontrolgroup/aad/search';
const CREATE_ACG_PATH = 'accesscontrolgroup';
const ACG_BY_ID_PATH = (id: string) => `accesscontrolgroup/${id}`;
const TENANT_GROUP_ROLE_PATH = 'tenantgrouprole';
const MY_TENANT_ROLE_PATH = 'tenantgrouprole/me';
const DATASET_GROUP_ROLE_PATH = (dataSetId: string) =>
  `datasets/grouprole/${dataSetId}`;
const DATASET_ACCESS_REQUEST_URL_PATH = (dataSetId: string) =>
  `${DATASET_GROUP_ROLE_PATH(dataSetId)}/accessrequesturl`;
const MY_DATASET_ROLE_PATH = (dataSetId: string) =>
  `datasets/grouprole/${dataSetId}/me`;

// ── Raw API types ─────────────────────────────────────────────────────────────

interface ApiAadGroupInfo {
  groupId?: string;
  displayName?: string;
}

interface ApiAccessControlGroup {
  id?: string;
  sgGroupId?: string;
  provider?: string;
  displayName?: string;
  link?: string;
  createdDateTime?: string;
  lastUpdatedDateTime?: string;
}

interface ApiTenantGroupRole {
  id?: string;
  tenantId?: string;
  roles?: {
    dataCreator?: string[] | null;
    dataUser?: string[] | null;
  };
  createdDateTime?: string;
  lastUpdatedDateTime?: string;
}

interface ApiMyTenantRoleResponse {
  isDataCreator?: boolean;
  isDataUser?: boolean;
  isAdmin?: boolean;
}

interface ApiDataSetCallerRoleResponse {
  roles?: string[] | null;
}

interface ApiDataSetAccessRequestUrlResponse {
  accessRequestUrl?: string | null;
}

interface ApiDatasetGroupRole {
  id?: string;
  tenantId?: string;
  roles?: {
    consumer?: string[] | null;
    restrictedConsumer?: string[] | null;
    annotator?: string[] | null;
    contributor?: string[] | null;
    owner?: string[] | null;
  };
  createdDateTime?: string;
  lastUpdatedDateTime?: string;
}

// ── Mapping helpers ───────────────────────────────────────────────────────────

function mapAadGroupInfo(api: ApiAadGroupInfo): AadGroupInfo {
  return {
    groupId: api.groupId ?? '',
    displayName: api.displayName ?? '',
  };
}

function mapAccessControlGroup(api: ApiAccessControlGroup): AccessControlGroup {
  return {
    id: api.id ?? '',
    sgGroupId: api.sgGroupId ?? '',
    provider: api.provider ?? '',
    displayName: api.displayName,
    link: api.link,
    createdDateTime: api.createdDateTime,
    lastUpdatedDateTime: api.lastUpdatedDateTime,
  };
}

function mapTenantGroupRole(api: ApiTenantGroupRole): TenantGroupRole {
  return {
    id: api.id,
    tenantId: api.tenantId,
    createdDateTime: api.createdDateTime,
    lastUpdatedDateTime: api.lastUpdatedDateTime,
    roles: {
      dataCreator: api.roles?.dataCreator ?? [],
      dataUser: api.roles?.dataUser ?? [],
    },
  };
}

function mapMyTenantRole(api: ApiMyTenantRoleResponse): MyTenantRoleResponse {
  return {
    isDataCreator: api.isDataCreator ?? false,
    isDataUser: api.isDataUser ?? false,
    isAdmin: api.isAdmin ?? false,
  };
}

function mapDataSetCallerRole(
  api: ApiDataSetCallerRoleResponse,
): DataSetCallerRoleResponse {
  return {
    roles: api.roles ?? [],
  };
}

function mapDataSetAccessRequestUrl(
  api: ApiDataSetAccessRequestUrlResponse,
): DataSetAccessRequestUrlResponse {
  const accessRequestUrl = api.accessRequestUrl?.trim();
  return {
    accessRequestUrl:
      accessRequestUrl === undefined || accessRequestUrl === ''
        ? undefined
        : accessRequestUrl,
  };
}

function mapDatasetGroupRole(api: ApiDatasetGroupRole): DatasetGroupRole {
  return {
    id: api.id,
    tenantId: api.tenantId,
    createdDateTime: api.createdDateTime,
    lastUpdatedDateTime: api.lastUpdatedDateTime,
    roles: {
      consumer: api.roles?.consumer ?? [],
      restrictedConsumer: api.roles?.restrictedConsumer ?? [],
      annotator: api.roles?.annotator ?? [],
      contributor: api.roles?.contributor ?? [],
      owner: api.roles?.owner ?? [],
    },
  };
}

// ── Service class ─────────────────────────────────────────────────────────────

export class AccessControlService implements IAccessControlService {
  constructor(private readonly client: IHttpClient) {}

  async searchAadGroups(displayName: string): Promise<AadGroupInfo[]> {
    const encoded = encodeURIComponent(displayName);
    const { data } = await this.client.get<ApiAadGroupInfo[]>(
      `${SEARCH_AAD_GROUPS_PATH}?displayName=${encoded}`,
    );
    return data.map(mapAadGroupInfo);
  }

  async createAccessControlGroup(
    req: CreateAccessControlGroupRequest,
  ): Promise<AccessControlGroup> {
    const { data } = await this.client.post<ApiAccessControlGroup>(
      CREATE_ACG_PATH,
      req,
    );
    return mapAccessControlGroup(data);
  }

  async getAccessControlGroup(id: string): Promise<AccessControlGroup> {
    const { data } = await this.client.get<ApiAccessControlGroup>(
      ACG_BY_ID_PATH(id),
    );
    return mapAccessControlGroup(data);
  }

  async updateAccessControlGroup(
    id: string,
    req: UpdateAccessControlGroupRequest,
  ): Promise<AccessControlGroup> {
    const { data } = await this.client.put<ApiAccessControlGroup>(
      ACG_BY_ID_PATH(id),
      req,
    );
    return mapAccessControlGroup(data);
  }

  async deleteAccessControlGroup(id: string): Promise<void> {
    await this.client.delete<void>(ACG_BY_ID_PATH(id));
  }

  async getTenantGroupRole(): Promise<TenantGroupRole> {
    const { data } = await this.client.get<ApiTenantGroupRole>(
      TENANT_GROUP_ROLE_PATH,
    );
    return mapTenantGroupRole(data);
  }

  async createTenantGroupRole(
    req: CreateTenantGroupRoleRequest,
    usage?: UsageContextOverrides,
  ): Promise<TenantGroupRole> {
    const { data } = await this.client.post<ApiTenantGroupRole>(
      TENANT_GROUP_ROLE_PATH,
      req,
      optionalUsageContextRequestOptions(
        ACCESS_CONTROL_USAGE_ACTIONS.SAVE_TENANT_PERMISSIONS,
        usage,
      ),
    );
    return mapTenantGroupRole(data);
  }

  async updateTenantGroupRole(
    req: UpdateTenantGroupRoleRequest,
    usage?: UsageContextOverrides,
  ): Promise<TenantGroupRole> {
    const { data } = await this.client.put<ApiTenantGroupRole>(
      TENANT_GROUP_ROLE_PATH,
      req,
      optionalUsageContextRequestOptions(
        ACCESS_CONTROL_USAGE_ACTIONS.SAVE_TENANT_PERMISSIONS,
        usage,
      ),
    );
    return mapTenantGroupRole(data);
  }

  async getMyTenantRole(): Promise<MyTenantRoleResponse> {
    const { data } =
      await this.client.get<ApiMyTenantRoleResponse>(MY_TENANT_ROLE_PATH);
    return mapMyTenantRole(data);
  }

  async getDatasetGroupRole(dataSetId: string): Promise<DatasetGroupRole> {
    const { data } = await this.client.get<ApiDatasetGroupRole>(
      DATASET_GROUP_ROLE_PATH(dataSetId),
    );
    return mapDatasetGroupRole(data);
  }

  async createDatasetGroupRole(
    req: CreateDatasetGroupRoleRequest,
    usage?: UsageContextOverrides,
  ): Promise<DatasetGroupRole> {
    const { data } = await this.client.post<ApiDatasetGroupRole>(
      DATASET_GROUP_ROLE_PATH(req.dataSetId),
      req,
      optionalUsageContextRequestOptions(
        ACCESS_CONTROL_USAGE_ACTIONS.SAVE_DATASET_PERMISSIONS,
        usage,
      ),
    );
    return mapDatasetGroupRole(data);
  }

  async updateDatasetGroupRole(
    dataSetId: string,
    req: UpdateDatasetGroupRoleRequest,
    usage?: UsageContextOverrides,
  ): Promise<DatasetGroupRole> {
    const { data } = await this.client.put<ApiDatasetGroupRole>(
      DATASET_GROUP_ROLE_PATH(dataSetId),
      req,
      optionalUsageContextRequestOptions(
        ACCESS_CONTROL_USAGE_ACTIONS.SAVE_DATASET_PERMISSIONS,
        usage,
      ),
    );
    return mapDatasetGroupRole(data);
  }

  async getDatasetAccessRequestUrl(
    dataSetId: string,
  ): Promise<DataSetAccessRequestUrlResponse> {
    const { data } = await this.client.get<ApiDataSetAccessRequestUrlResponse>(
      DATASET_ACCESS_REQUEST_URL_PATH(dataSetId),
    );
    return mapDataSetAccessRequestUrl(data);
  }

  async getMyDatasetRole(
    dataSetId: string,
  ): Promise<DataSetCallerRoleResponse> {
    const { data } = await this.client.get<ApiDataSetCallerRoleResponse>(
      MY_DATASET_ROLE_PATH(dataSetId),
    );
    return mapDataSetCallerRole(data);
  }
}
